import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, ButtonBase, styled } from "@mui/material";

import { Link } from "react-router-dom";

import {
    COLORS as MODAL_COLORS,
    RADIUS as MODAL_RADIUS,
    SHADOW as MODAL_SHADOW,
    TYPOGRAPHY as MODAL_TYPOGRAPHY,
} from "components/organisms/CreationModal/CreationModal.constants";

import {
    COLORS,
    RADIUS,
    SHADOW,
    TYPOGRAPHY,
} from "../../SignalWizard.constants";
import { transientOptions } from "../../SignalWizard.styled";

export const Header = styled("header")(({ theme }) => ({
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    width: "100%",
    height: 56,
    padding: "0 16px",
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderBottom: `1px solid rgba(198, 198, 205, 0.3)`,
    boxShadow: SHADOW.sm,
    [theme.breakpoints.up("md")]: {
        padding: "0 24px",
    },
}));

export const Brand = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
});

export const BrandLink = styled(Link)({
    display: "flex",
    alignItems: "center",
    borderRadius: RADIUS.control,
    transition: "opacity 150ms",
    "&:hover": { opacity: 0.8 },
});

export const BrandLogo = styled("img")(({ theme }) => ({
    display: "block",
    height: 28,
    width: "auto",
    [theme.breakpoints.down("sm")]: { height: 22 },
}));

export const Separator = styled("span")({
    color: COLORS.outlineVariant,
    ...TYPOGRAPHY.bodySm,
});

export const Badge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 6px",
    borderRadius: RADIUS.control,
    backgroundColor: COLORS.surfaceLow,
    color: COLORS.onSurfaceVariant,
    border: `1px solid rgba(198, 198, 205, 0.3)`,
    whiteSpace: "nowrap",
    ...TYPOGRAPHY.labelSm,
});

/**
 * Step manager. Mirrors the `StepIndicator` used in the create source /
 * destination modal headers so both step UIs share one look.
 */
export const Stepper = styled("nav")(({ theme }) => ({
    display: "none",
    alignItems: "center",
    padding: 4,
    borderRadius: MODAL_RADIUS.md,
    backgroundColor: MODAL_COLORS.slate50,
    border: `1px solid rgba(226, 232, 240, 0.7)`,
    [theme.breakpoints.up("md")]: {
        display: "flex",
    },
}));

export const StepItem = styled(
    ButtonBase,
    transientOptions,
)<{
    $state: "done" | "active" | "upcoming";
}>(({ $state }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 6,
    whiteSpace: "nowrap",
    transition: "color 150ms, background-color 150ms",
    ...MODAL_TYPOGRAPHY.labelXs,
    fontSize: 12,
    cursor: $state === "done" ? "pointer" : "default",
    ...($state === "active" && {
        fontWeight: 600,
        color: MODAL_COLORS.primaryDark,
        backgroundColor: MODAL_COLORS.primarySoft,
        border: `1px solid ${MODAL_COLORS.primarySoftBorder}`,
        boxShadow: MODAL_SHADOW.xs,
    }),
    ...($state === "done" && {
        color: MODAL_COLORS.slate500,
        "&:hover": { color: MODAL_COLORS.primaryDark },
        "& svg": { fontSize: 14, color: MODAL_COLORS.primary },
    }),
    ...($state === "upcoming" && {
        color: MODAL_COLORS.slate400,
    }),
    "&.Mui-disabled": { pointerEvents: "none" },
}));

export const ActiveDot = styled("span")({
    width: 6,
    height: 6,
    borderRadius: RADIUS.full,
    backgroundColor: MODAL_COLORS.primary,
});

export const StepChevron = styled(ChevronRightIcon)({
    fontSize: 14,
    color: MODAL_COLORS.slate400,
    margin: "0 4px",
});

export const ExitButton = styled(ButtonBase)({
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: RADIUS.control,
    color: COLORS.onSurfaceVariant,
    transition: "color 150ms, background-color 150ms",
    ...TYPOGRAPHY.labelMd,
    "& svg": { fontSize: 18, color: COLORS.outline },
    "&:hover": {
        color: COLORS.primary,
        backgroundColor: COLORS.surfaceLow,
        "& svg, & kbd": { color: COLORS.primary },
    },
    "&:active": { transform: "scale(0.99)" },
});

export const Kbd = styled("kbd")(({ theme }) => ({
    display: "none",
    padding: "0 4px",
    fontSize: 10,
    color: COLORS.onSurfaceVariant,
    backgroundColor: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.control,
    boxShadow: "none",
    [theme.breakpoints.up("sm")]: {
        display: "inline-block",
    },
}));
